/* ----------------------------------------------------------------------------
   Visuels présentationnels de la page d'accueil : icônes au trait fin et mocks
   SVG (carte, tableau, graphique, fichier, courbes de niveau). Extraits de
   landing.tsx pour garder la page concentrée sur la structure et la copy.
   Tous décoratifs (aria-hidden) sauf usage explicite.
---------------------------------------------------------------------------- */

export type IconProps = {readonly className?: string}

export function ArrowRight() {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M5 12h14' /><path d='m13 6 6 6-6 6' />
    </svg>
  )
}

export function DownloadIcon() {
  return (
    <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M12 3v12' /><path d='m7 10 5 5 5-5' /><path d='M5 21h14' />
    </svg>
  )
}

export function SourcesIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <ellipse cx='12' cy='5' rx='8' ry='3' /><path d='M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5' /><path d='M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6' />
    </svg>
  )
}

export function ProduceIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <rect x='3' y='3' width='8' height='8' rx='1.5' /><rect x='13' y='3' width='8' height='5' rx='1.5' />
      <rect x='13' y='10' width='8' height='11' rx='1.5' /><rect x='3' y='13' width='8' height='8' rx='1.5' />
    </svg>
  )
}

export function ExplainIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z' /><circle cx='12' cy='12' r='3' />
    </svg>
  )
}

export function ThemeIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M4 9h16' /><path d='M4 15h16' /><path d='M10 3 8 21' /><path d='M16 3l-2 18' />
    </svg>
  )
}

export function OrgIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <rect x='4' y='3' width='16' height='18' rx='1.5' /><path d='M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2' />
    </svg>
  )
}

export function PersonIcon() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' /><circle cx='12' cy='7' r='4' />
    </svg>
  )
}

export function SparkleIcon() {
  return (
    <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M12 3v4M12 17v4M5 12H3M21 12h-2M6.3 6.3 4.9 4.9M19.1 19.1l-1.4-1.4M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4' />
    </svg>
  )
}

export function FlowArrow() {
  return (
    <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M5 12h14' /><path d='m13 6 6 6-6 6' />
    </svg>
  )
}

// Carte abstraite : régions, points géocodés, courbes de niveau. Sert d'objet
// « carte » et d'aperçu dans le panneau produit du hero.
export function MapViz({className}: IconProps) {
  return (
    <svg className={className} viewBox='0 0 320 140' preserveAspectRatio='xMidYMid slice' aria-hidden='true'>
      <rect width='320' height='140' fill='#f1f6f3' />
      <path d='M-10 95 L80 70 L150 88 L230 60 L330 80 L330 150 L-10 150 Z' fill='#dcebe2' />
      <path d='M-10 60 L60 48 L140 60 L210 38 L300 52 L330 44 L330 96 L230 76 L150 100 L70 84 L-10 104 Z' fill='#cfe2ef' opacity='0.75' />
      <g stroke='#9fb9c9' strokeWidth='1' fill='none' opacity='0.55'>
        <path d='M-10 78 C60 66 110 90 180 74 C240 60 290 80 330 70' />
        <path d='M-10 100 C70 90 120 110 200 96 C260 86 300 100 330 94' />
        <path d='M-10 56 C50 46 120 64 190 50 C250 38 300 54 330 46' />
      </g>
      <g>
        <circle cx='96' cy='78' r='5' fill='#0d5a9e' />
        <circle cx='168' cy='66' r='5' fill='#1b7a66' />
        <circle cx='232' cy='84' r='5' fill='#0d5a9e' />
        <circle cx='266' cy='58' r='4' fill='#1b7a66' />
        <circle cx='132' cy='94' r='4' fill='#0d5a9e' />
      </g>
    </svg>
  )
}

export function TableViz({className}: IconProps) {
  const rows = [0, 1, 2, 3]
  return (
    <svg className={className} viewBox='0 0 320 140' preserveAspectRatio='xMidYMid slice' aria-hidden='true'>
      <rect width='320' height='140' fill='#ffffff' />
      <rect x='0' y='0' width='320' height='26' fill='#f3f5f3' />
      <g fill='#9aa7b2'>
        <rect x='18' y='11' width='52' height='5' rx='2.5' />
        <rect x='120' y='11' width='44' height='5' rx='2.5' />
        <rect x='210' y='11' width='40' height='5' rx='2.5' />
      </g>
      {rows.map(r => (
        <g key={r} transform={`translate(0 ${34 + (r * 26)})`}>
          <rect x='18' y='6' width='70' height='6' rx='3' fill='#c8d1d8' />
          <rect x='120' y='6' width='52' height='6' rx='3' fill='#dbe1e6' />
          <rect x='210' y='6' width='34' height='6' rx='3' fill={r % 2 === 0 ? '#bfe0cd' : '#dbe1e6'} />
          <line x1='0' y1='22' x2='320' y2='22' stroke='#eef1f0' strokeWidth='1' />
        </g>
      ))}
    </svg>
  )
}

export function ChartViz({className}: IconProps) {
  // x positionne la barre, highlight met en avant la valeur dominante (vert).
  const bars = [
    {x: 28, h: 54},
    {x: 68, h: 78},
    {x: 108, h: 40},
    {x: 148, h: 92},
    {x: 188, h: 66},
    {x: 228, h: 108, highlight: true},
    {x: 268, h: 84}
  ]
  return (
    <svg className={className} viewBox='0 0 320 140' preserveAspectRatio='xMidYMid slice' aria-hidden='true'>
      <rect width='320' height='140' fill='#ffffff' />
      <g stroke='#eef1f0' strokeWidth='1'>
        <line x1='0' y1='40' x2='320' y2='40' /><line x1='0' y1='80' x2='320' y2='80' /><line x1='0' y1='118' x2='320' y2='118' />
      </g>
      {bars.map(bar => (
        <rect key={bar.x} x={bar.x} y={120 - bar.h} width='22' height={bar.h} rx='3' fill={bar.highlight ? '#1b7a66' : '#0d5a9e'} opacity={bar.highlight ? 1 : 0.78} />
      ))}
    </svg>
  )
}

export function FileViz({className}: IconProps) {
  return (
    <svg className={className} viewBox='0 0 320 140' preserveAspectRatio='xMidYMid slice' aria-hidden='true'>
      <rect width='320' height='140' fill='#f7f9f8' />
      <g transform='translate(118 26)'>
        <path d='M6 0 h54 l24 24 v66 a4 4 0 0 1-4 4 H6 a4 4 0 0 1-4-4 V4 a4 4 0 0 1 4-4 Z' fill='#ffffff' stroke='#cdd6dc' strokeWidth='1.5' />
        <path d='M60 0 v20 a4 4 0 0 0 4 4 h20' fill='none' stroke='#cdd6dc' strokeWidth='1.5' />
        <g fill='#9fb1bd'>
          <rect x='16' y='44' width='52' height='5' rx='2.5' /><rect x='16' y='56' width='40' height='5' rx='2.5' /><rect x='16' y='68' width='48' height='5' rx='2.5' />
        </g>
        <rect x='16' y='84' width='30' height='12' rx='3' fill='#1b7a66' />
      </g>
    </svg>
  )
}

// Bandeau de courbes de niveau pour le CTA final.
export function ContourField() {
  return (
    <svg className='ld-contour' viewBox='0 0 1200 400' preserveAspectRatio='xMidYMid slice' fill='none' stroke='currentColor' strokeWidth='1.5' aria-hidden='true'>
      {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
        <path
          key={i}
          d={`M-50 ${120 + (i * 26)} C 250 ${60 + (i * 26)}, 450 ${200 + (i * 22)}, 700 ${140 + (i * 24)} S 1100 ${80 + (i * 26)}, 1260 ${160 + (i * 24)}`}
        />
      ))}
    </svg>
  )
}
